import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AtSign, Loader2, Trash2, Search, Eye, Lock, Plus, X, History, Ban, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NicknamesTab({ language }) {
  const isLV = language === 'lv';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [target, setTarget] = useState(null);
  const [releasing, setReleasing] = useState(false);

  // Reserved words — nicknames blocked from registration.
  const [reserved, setReserved] = useState([]);
  const [newWord, setNewWord] = useState('');
  const [matchMode, setMatchMode] = useState('exact'); // exact | prefix | contains
  const [addingWord, setAddingWord] = useState(false);

  // Moderation history (block / unblock / release).
  const [audit, setAudit] = useState([]);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/nickname-audit`);
      setAudit(data.entries || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadReserved = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reserved-words`);
      setReserved(data.words || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const addWord = async (e) => {
    e?.preventDefault?.();
    const w = newWord.trim().replace(/^@/, '').toLowerCase();
    if (!w) return;
    try {
      setAddingWord(true);
      await axios.post(`${API}/admin/reserved-words`, { word: w, match: matchMode });
      toast.success(isLV ? `"${w}" bloķēts` : `"${w}" blocked`);
      setNewWord('');
      loadReserved();
      loadAudit();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingWord(false);
    }
  };

  const removeWord = async (w) => {
    try {
      await axios.delete(`${API}/admin/reserved-words/${encodeURIComponent(w)}`);
      setReserved((prev) => prev.filter((x) => x.word !== w));
      loadAudit();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = useCallback(async (term) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/nicknames`, { params: { q: term || '' } });
      setItems(data.nicknames || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);
  useEffect(() => { loadReserved(); }, [loadReserved]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  useEffect(() => {
    const tid = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const release = async () => {
    if (!target) return;
    try {
      setReleasing(true);
      await axios.delete(`${API}/admin/nicknames/${target.user_id}`);
      toast.success(isLV ? `Iesauka @${target.nickname} atbrīvota` : `Nickname @${target.nickname} released`);
      setItems((prev) => prev.filter((x) => x.user_id !== target.user_id));
      setTarget(null);
      loadAudit();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReleasing(false);
    }
  };

  return (
    <div data-testid="admin-nicknames-tab">
      {/* ── Reserved / blocked words ────────────────────────────────── */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-5 h-5 text-[#D97736]" />
          <h2 className="font-outfit text-xl font-bold text-[#121212]">
            {isLV ? 'Bloķētie vārdi' : 'Reserved words'}
          </h2>
          <span className="text-sm text-[#5C5C5C]">({reserved.length})</span>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-4">
          {isLV
            ? 'Šīs iesaukas nevar reģistrēt neviens lietotājs (piem. admin, kolv).'
            : 'Nobody can register these nicknames (e.g. admin, kolv).'}
        </p>

        <form onSubmit={addWord} className="mb-4 max-w-md">
          <div className="inline-flex rounded-lg border border-[#E5E4E0] bg-[#FAFAFA] p-0.5 mb-2" data-testid="reserved-match-toggle">
            {[
              { id: 'exact', lv: 'Precīzs', en: 'Exact' },
              { id: 'prefix', lv: 'Sākas ar', en: 'Starts with' },
              { id: 'contains', lv: 'Satur', en: 'Contains' },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMatchMode(m.id)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${matchMode === m.id ? 'bg-[#D97736] text-white' : 'text-[#5C5C5C] hover:text-[#121212]'}`}
                data-testid={`match-mode-${m.id}`}
              >
                {isLV ? m.lv : m.en}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#9CA3AF] mb-2">
            {matchMode === 'exact' && (isLV ? 'Bloķē tieši šo iesauku.' : 'Blocks exactly this nickname.')}
            {matchMode === 'prefix' && (isLV ? 'Bloķē visas iesaukas, kas sākas ar šo vārdu (piem. kolv → kolv_official).' : 'Blocks any nickname starting with this word.')}
            {matchMode === 'contains' && (isLV ? 'Bloķē visas iesaukas, kas satur šo vārdu jebkurā vietā.' : 'Blocks any nickname containing this word anywhere.')}
          </p>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5C5C]" />
              <Input
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                placeholder={isLV ? 'Pievienot bloķētu vārdu…' : 'Add a blocked word…'}
                className="pl-10"
                maxLength={20}
                data-testid="reserved-word-input"
              />
            </div>
            <Button
              type="submit"
              disabled={addingWord || !newWord.trim()}
              className="bg-[#D97736] hover:bg-[#c56729] text-white"
              data-testid="reserved-word-add-btn"
            >
              {addingWord ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              <span className="ml-1">{isLV ? 'Bloķēt' : 'Block'}</span>
            </Button>
          </div>
        </form>

        {reserved.length === 0 ? (
          <p className="text-sm text-[#5C5C5C]">{isLV ? 'Nav bloķētu vārdu' : 'No reserved words'}</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="reserved-words-list">
            {reserved.map((w) => {
              const mode = w.match || 'exact';
              const modeLabel = mode === 'prefix' ? (isLV ? 'sākas ar' : 'starts with') : mode === 'contains' ? (isLV ? 'satur' : 'contains') : null;
              return (
                <span
                  key={w.word}
                  className="inline-flex items-center gap-1.5 bg-[#F3F2EE] border border-[#E5E4E0] rounded-full pl-3 pr-1.5 py-1 text-sm text-[#121212]"
                  data-testid={`reserved-word-${w.word}`}
                >
                  <span className="font-medium">
                    {mode === 'contains' ? `…${w.word}…` : mode === 'prefix' ? `@${w.word}…` : `@${w.word}`}
                  </span>
                  {modeLabel && (
                    <span className="text-[10px] text-[#D97736] bg-[#D97736]/10 px-1.5 py-0.5 rounded-full">{modeLabel}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeWord(w.word)}
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[#5C5C5C] hover:bg-red-100 hover:text-red-600 transition-colors"
                    title={isLV ? 'Atbloķēt' : 'Unblock'}
                    data-testid={`reserved-word-remove-${w.word}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Taken nicknames (release on abuse) ──────────────────────── */}
      <div className="flex items-center gap-2 mb-4">
        <AtSign className="w-5 h-5 text-[#D97736]" />
        <h2 className="font-outfit text-xl font-bold text-[#121212]">
          {isLV ? 'Aizņemtās iesaukas' : 'Taken nicknames'}
        </h2>
        <span className="text-sm text-[#5C5C5C]">({items.length})</span>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5C5C]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={isLV ? 'Meklēt iesauku, vārdu vai e-pastu…' : 'Search nickname, name or email…'}
          className="pl-10"
          data-testid="nicknames-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-[#D97736]" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-center text-[#5C5C5C] py-12 text-sm">{isLV ? 'Nav aizņemtu iesauku' : 'No taken nicknames'}</p>
      ) : (
        <div className="overflow-x-auto border border-[#E5E4E0] rounded-2xl bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E4E0] text-left text-[#5C5C5C]">
                <th className="px-4 py-3 font-medium">{isLV ? 'Iesauka' : 'Nickname'}</th>
                <th className="px-4 py-3 font-medium">{isLV ? 'Lietotājs' : 'User'}</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">{isLV ? 'E-pasts' : 'Email'}</th>
                <th className="px-4 py-3 font-medium text-right">{isLV ? 'Darbība' : 'Action'}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.user_id} className="border-b border-[#F3F2EE] last:border-0 hover:bg-[#FAFAFA]" data-testid={`nickname-row-${it.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#D97736]">@{it.nickname}</span>
                      {it.shown_publicly && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#4A6B53] bg-[#4A6B53]/10 px-1.5 py-0.5 rounded-full" title={isLV ? 'Rādīts publiski' : 'Shown publicly'}>
                          <Eye className="w-3 h-3" /> {isLV ? 'Publisks' : 'Public'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#121212]">{it.name || '—'}</td>
                  <td className="px-4 py-3 text-[#5C5C5C] hidden sm:table-cell">{it.email || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setTarget(it)}
                      data-testid={`release-nickname-${it.user_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      {isLV ? 'Atbrīvot' : 'Release'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Moderation history ──────────────────────────────────────── */}
      <div className="flex items-center gap-2 mt-10 mb-4">
        <History className="w-5 h-5 text-[#D97736]" />
        <h2 className="font-outfit text-xl font-bold text-[#121212]">
          {isLV ? 'Iesauku vēsture' : 'Nickname history'}
        </h2>
        <span className="text-sm text-[#5C5C5C]">({audit.length})</span>
      </div>

      {audit.length === 0 ? (
        <p className="text-sm text-[#5C5C5C]">{isLV ? 'Nav darbību vēstures' : 'No history yet'}</p>
      ) : (
        <div className="overflow-x-auto border border-[#E5E4E0] rounded-2xl bg-white" data-testid="nickname-audit-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E4E0] text-left text-[#5C5C5C]">
                <th className="px-4 py-3 font-medium">{isLV ? 'Darbība' : 'Action'}</th>
                <th className="px-4 py-3 font-medium">{isLV ? 'Vērtība' : 'Value'}</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">{isLV ? 'Administrators' : 'Admin'}</th>
                <th className="px-4 py-3 font-medium text-right">{isLV ? 'Datums' : 'Date'}</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((e, i) => {
                const meta = {
                  block: { icon: Ban, cls: 'text-red-600 bg-red-50', lv: 'Bloķēts', en: 'Blocked' },
                  unblock: { icon: ShieldOff, cls: 'text-[#4A6B53] bg-[#4A6B53]/10', lv: 'Atbloķēts', en: 'Unblocked' },
                  release: { icon: Trash2, cls: 'text-[#D97736] bg-[#D97736]/10', lv: 'Atbrīvota iesauka', en: 'Released' },
                }[e.action] || { icon: History, cls: 'text-[#5C5C5C] bg-[#F3F2EE]', lv: e.action, en: e.action };
                const Icon = meta.icon;
                const modeLabel = e.match === 'prefix' ? (isLV ? 'sākas ar' : 'starts with') : e.match === 'contains' ? (isLV ? 'satur' : 'contains') : null;
                const value = e.action === 'release' ? `@${e.nickname}` : (e.match === 'contains' ? `…${e.word}…` : e.match === 'prefix' ? `@${e.word}…` : `@${e.word}`);
                return (
                  <tr key={i} className="border-b border-[#F3F2EE] last:border-0 hover:bg-[#FAFAFA]" data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${meta.cls}`}>
                        <Icon className="w-3.5 h-3.5" /> {isLV ? meta.lv : meta.en}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-[#121212]">{value}</span>
                        {modeLabel && <span className="text-[10px] text-[#D97736] bg-[#D97736]/10 px-1.5 py-0.5 rounded-full">{modeLabel}</span>}
                        {e.action === 'release' && e.target_name && <span className="text-[#5C5C5C]">· {e.target_name}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#5C5C5C] hidden sm:table-cell">
                      {e.admin_name || e.admin_email || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-[#5C5C5C] whitespace-nowrap">
                      {e.created_at ? new Date(e.created_at).toLocaleString(isLV ? 'lv-LV' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}


      <AlertDialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <AlertDialogContent data-testid="release-nickname-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{isLV ? 'Atbrīvot iesauku?' : 'Release nickname?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {isLV
                ? `Iesauka @${target?.nickname} tiks noņemta lietotājam ${target?.name || ''}. Ja tā tika rādīta publiski, publiskais vārds atgriezīsies uz pilno vārdu. Iesauka kļūs brīva citiem.`
                : `The nickname @${target?.nickname} will be removed from ${target?.name || ''}. If it was shown publicly, their public name falls back to full name. The handle becomes available to others.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="release-cancel-btn">{isLV ? 'Atcelt' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); release(); }}
              disabled={releasing}
              className="bg-red-600 hover:bg-red-700"
              data-testid="release-confirm-btn"
            >
              {releasing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isLV ? 'Atbrīvot' : 'Release')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
